/**
 * usePickerOptions hook (P3-A).
 *
 * Debounced fetch picker results với in-memory cache theo (kind, q, parentId, filters).
 * Cache TTL 60s — đủ cho 1 session UX nhưng không stale quá lâu.
 */
import * as React from "react";

import {
  type PickerItem,
  type PickerKind,
  type PickerOptions,
  pickerApi,
} from "@/api/picker";

interface CacheEntry {
  items: PickerItem[];
  hasMore: boolean;
  ts: number;
}

const CACHE_TTL_MS = 60_000;
const CACHE = new Map<string, CacheEntry>();

function cacheKey(kind: PickerKind, options: PickerOptions): string {
  return JSON.stringify([
    kind,
    options.q ?? "",
    options.parentId ?? "",
    options.filters ?? {},
    options.limit ?? 25,
  ]);
}

interface State {
  items: PickerItem[];
  hasMore: boolean;
  loading: boolean;
  error: string | null;
}

interface Options extends PickerOptions {
  /** Bật/tắt fetch — vd disable cho cascading khi parent chưa chọn. */
  enabled?: boolean;
  /** Debounce ms cho query thay đổi. */
  debounceMs?: number;
}

export function usePickerOptions(
  kind: PickerKind,
  options: Options = {},
): State & { refetch: () => void } {
  const { enabled = true, debounceMs = 200, ...rest } = options;

  const [state, setState] = React.useState<State>({
    items: [],
    hasMore: false,
    loading: false,
    error: null,
  });

  // Stabilize options for cache key
  const key = React.useMemo(() => cacheKey(kind, rest), [kind, rest]);
  const [reloadTick, setReloadTick] = React.useState(0);

  React.useEffect(() => {
    if (!enabled) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    // Cache hit
    const cached = CACHE.get(key);
    const now = Date.now();
    if (cached && now - cached.ts < CACHE_TTL_MS && reloadTick === 0) {
      setState({
        items: cached.items,
        hasMore: cached.hasMore,
        loading: false,
        error: null,
      });
      return;
    }

    let aborted = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    const handle = window.setTimeout(() => {
      pickerApi
        .search(kind, rest)
        .then((res) => {
          if (aborted) return;
          CACHE.set(key, { items: res.items, hasMore: res.has_more, ts: Date.now() });
          setState({
            items: res.items,
            hasMore: res.has_more,
            loading: false,
            error: null,
          });
        })
        .catch((e: unknown) => {
          if (aborted) return;
          const msg = e instanceof Error ? e.message : String(e);
          setState({ items: [], hasMore: false, loading: false, error: msg });
        });
    }, debounceMs);

    return () => {
      aborted = true;
      window.clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, reloadTick]);

  const refetch = React.useCallback(() => {
    CACHE.delete(key);
    setReloadTick((t) => t + 1);
  }, [key]);

  return { ...state, refetch };
}

/** Reset toàn bộ cache — gọi khi save form/workflow xong. */
export function clearPickerCache(kind?: PickerKind): void {
  if (!kind) {
    CACHE.clear();
    return;
  }
  for (const k of Array.from(CACHE.keys())) {
    if (k.startsWith(`["${kind}"`)) CACHE.delete(k);
  }
}
